/**
 * Client-side image resize & compression.
 * Phone photos are typically 5-15 MB. Resize to max 1600px (longest side) at 80% JPEG
 * → typically ~150-400 KB. Returns a base64 data URL.
 *
 * - Supports HEIC/JPEG/PNG (browser-native).
 * - Preserves orientation via the canvas pipeline.
 * - Falls back to original data URL if browser image decoding fails.
 */

const MAX_INPUT_BYTES = 25 * 1024 * 1024; // 25 MB raw upload
const DEFAULT_MAX_SIZE = 1600; // longest edge
const DEFAULT_QUALITY = 0.82;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Soubor nelze přečíst"));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Obrázek nelze načíst (formát?)"));
    img.src = src;
  });

const canvasToDataUrl = (canvas, mime, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error("Komprese selhala")); return; }
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("Konverze selhala"));
        reader.readAsDataURL(blob);
      },
      mime,
      quality
    );
  });

/**
 * Resize + compress an image file. Returns a JPEG data URL.
 * @param {File} file
 * @param {{maxSize?: number, quality?: number}} opts
 */
export async function resizeImage(file, opts = {}) {
  if (!file) throw new Error("Žádný soubor");
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error(`Soubor je příliš velký (${(file.size / 1024 / 1024).toFixed(1)} MB, max 25 MB)`);
  }
  const maxSize = opts.maxSize ?? DEFAULT_MAX_SIZE;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  const dataUrl = await fileToDataUrl(file);

  // Non-image (PDF etc.) — return as-is so caller can choose to allow/reject.
  if (!file.type.startsWith("image/")) {
    return dataUrl;
  }

  try {
    const img = await loadImage(dataUrl);
    let { width, height } = img;
    if (width === 0 || height === 0) return dataUrl;
    const longest = Math.max(width, height);
    if (longest > maxSize) {
      const ratio = maxSize / longest;
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // White background for transparent PNGs (JPEG can't handle alpha)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    return await canvasToDataUrl(canvas, "image/jpeg", quality);
  } catch (e) {
    // Browser couldn't decode (e.g., HEIC on Firefox) — fall back to original
    console.warn("Image resize fallback:", e.message);
    return dataUrl;
  }
}

/** Helper for input[type=file] onChange when you want a single image */
export const handleSingleImage = async (event, options) => {
  const file = event.target?.files?.[0];
  if (!file) return null;
  return resizeImage(file, options);
};
